import { useEffect, useState, useRef } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { formatCLP, formatNumber, formatRut } from "@/lib/rut";
import {
  Upload,
  Users,
  FileText,
  Inbox,
  BarChart3,
  Plus,
  Trash2,
  Pencil,
  Search,
  Download,
  FileSpreadsheet,
  Eye,
  KeyRound,
  History,
  RefreshCw,
} from "lucide-react";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("dashboard");

  useEffect(() => {
    refreshStats();
  }, []);

  const refreshStats = () =>
    api
      .get("/admin/stats")
      .then((r) => setStats(r.data))
      .catch((e) => toast.error(formatApiError(e)));

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="admin-dashboard">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#475569]">
            Panel de administración
          </div>
          <h1 className="mt-2 font-heading text-3xl lg:text-4xl font-bold text-[#0A2540] tracking-tight">
            SmartNation · Control
          </h1>
          <p className="text-sm text-[#475569] mt-1">
            Administre accionistas, compras, comunicados y solicitudes.
          </p>
        </div>

        {/* KPIs */}
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Kpi label="Accionistas" value={formatNumber(stats?.total_shareholders)} icon={<Users size={18} />} testid="kpi-shareholders" />
          <Kpi label="Acciones emitidas" value={formatNumber(stats?.total_acciones)} icon={<BarChart3 size={18} />} testid="kpi-acciones" />
          <Kpi label="Monto transado" value={formatCLP(stats?.total_monto)} icon={<BarChart3 size={18} />} testid="kpi-monto" />
          <Kpi label="Contratos suscritos" value={formatNumber(stats?.total_contratos)} icon={<FileText size={18} />} testid="kpi-contratos" />
          <Kpi label="Solicitudes pendientes" value={formatNumber(stats?.total_solicitudes_pendientes)} icon={<Inbox size={18} />} testid="kpi-pending" />
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mt-10">
          <TabsList className="bg-white border border-[#E2E8F0] rounded-sm p-1 flex flex-wrap h-auto">
            <TabsTrigger value="dashboard" data-testid="admin-tab-dashboard" className="rounded-sm data-[state=active]:bg-[#0A2540] data-[state=active]:text-white">Carga Excel</TabsTrigger>
            <TabsTrigger value="shareholders" data-testid="admin-tab-shareholders" className="rounded-sm data-[state=active]:bg-[#0A2540] data-[state=active]:text-white">Accionistas</TabsTrigger>
            <TabsTrigger value="purchases" data-testid="admin-tab-purchases" className="rounded-sm data-[state=active]:bg-[#0A2540] data-[state=active]:text-white">Compras</TabsTrigger>
            <TabsTrigger value="announcements" data-testid="admin-tab-announcements" className="rounded-sm data-[state=active]:bg-[#0A2540] data-[state=active]:text-white">Comunicados</TabsTrigger>
            <TabsTrigger value="solicitudes" data-testid="admin-tab-solicitudes" className="rounded-sm data-[state=active]:bg-[#0A2540] data-[state=active]:text-white">Solicitudes</TabsTrigger>
            <TabsTrigger value="audit" data-testid="admin-tab-audit" className="rounded-sm data-[state=active]:bg-[#0A2540] data-[state=active]:text-white">Auditoría</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="mt-6">
            <ExcelUploadSection onSuccess={refreshStats} />
          </TabsContent>
          <TabsContent value="shareholders" className="mt-6">
            <ShareholdersSection onChange={refreshStats} />
          </TabsContent>
          <TabsContent value="purchases" className="mt-6">
            <PurchasesSection onChange={refreshStats} />
          </TabsContent>
          <TabsContent value="announcements" className="mt-6">
            <AnnouncementsSection onChange={refreshStats} />
          </TabsContent>
          <TabsContent value="solicitudes" className="mt-6">
            <SolicitudesSection onChange={refreshStats} />
          </TabsContent>
          <TabsContent value="audit" className="mt-6">
            <AuditLogSection />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Kpi({ label, value, icon, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#E2E8F0] rounded-sm p-6">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#475569]">{label}</div>
        <div className="h-8 w-8 rounded-sm bg-[#F8F9FA] text-[#0A2540] flex items-center justify-center">{icon}</div>
      </div>
      <div className="mt-4 font-heading text-3xl font-bold text-[#0A2540]">{value ?? "—"}</div>
    </div>
  );
}

// ---------- Excel Upload ----------
function ExcelUploadSection({ onSuccess }) {
  const inputRef = useRef();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) setFile(f);
  };

  const submit = async () => {
    if (!file) return toast.error("Seleccione un archivo Excel");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload-excel", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      toast.success("Excel procesado correctamente");
      onSuccess?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-sm p-8">
        <div className="flex items-center gap-3">
          <FileSpreadsheet size={22} className="text-[#0A2540]" />
          <h3 className="font-heading text-xl font-semibold text-[#0A2540]">
            Carga masiva de accionistas
          </h3>
        </div>
        <p className="mt-2 text-sm text-[#475569]">
          Suba el archivo Excel con el listado de accionistas. El RUT será normalizado
          (se eliminan los puntos) y se crearán o actualizarán automáticamente las cuentas, compras y
          datos asociados. La contraseña inicial de cada accionista es su RUT normalizado.
        </p>

        <div
          className="mt-6 border-2 border-dashed border-[#0A2540]/20 rounded-sm p-10 text-center hover:bg-[#F8F9FA] cursor-pointer"
          onClick={() => inputRef.current?.click()}
          data-testid="excel-dropzone"
        >
          <Upload size={32} className="mx-auto text-[#0A2540]" />
          <div className="mt-3 font-medium text-[#0A2540]">
            {file ? file.name : "Haga clic para seleccionar un Excel"}
          </div>
          <div className="text-xs text-[#475569] mt-1">Formatos .xlsx / .xls</div>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={onPick}
            data-testid="excel-file-input"
          />
        </div>

        <div className="mt-6 flex items-center gap-3">
          <Button
            onClick={submit}
            disabled={!file || loading}
            className="rounded-sm bg-[#0A2540] hover:bg-[#051626] text-white"
            data-testid="excel-submit-button"
          >
            {loading ? "Procesando…" : "Procesar Excel"}
          </Button>
          {file && (
            <Button
              variant="outline"
              onClick={() => {
                setFile(null);
                if (inputRef.current) inputRef.current.value = "";
              }}
              className="rounded-sm border-[#E2E8F0]"
            >
              Quitar archivo
            </Button>
          )}
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-sm p-6">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#475569]">
          Resultado último procesamiento
        </div>
        {result ? (
          <ul className="mt-4 space-y-2 text-sm" data-testid="excel-result">
            <ResultRow label="Accionistas creados" value={result.created_shareholders} />
            <ResultRow label="Accionistas actualizados" value={result.updated_shareholders} />
            <ResultRow label="Cuentas creadas" value={result.created_users} />
            <ResultRow label="Compras creadas" value={result.created_purchases} />
            <ResultRow label="Filas omitidas" value={result.skipped_rows} muted />
          </ul>
        ) : (
          <p className="mt-4 text-sm text-[#475569]">Sin cargas recientes.</p>
        )}
      </div>
    </div>
  );
}

function ResultRow({ label, value, muted }) {
  return (
    <li className="flex items-center justify-between border-b border-[#E2E8F0] pb-2">
      <span className={muted ? "text-[#475569]" : "text-[#0F172A]"}>{label}</span>
      <span className="font-heading font-semibold text-[#0A2540]">{value ?? 0}</span>
    </li>
  );
}

// ---------- Shareholders ----------
function ShareholdersSection({ onChange }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  // Range filters
  const [contratosMin, setContratosMin] = useState("");
  const [contratosMax, setContratosMax] = useState("");
  const [montoMin, setMontoMin] = useState("");
  const [montoMax, setMontoMax] = useState("");

  const load = () => {
    setLoading(true);
    api
      .get("/shareholders")
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = items.filter((s) => {
    const t = q.toLowerCase();
    const textMatch =
      !t ||
      s.nombre?.toLowerCase().includes(t) ||
      s.rut?.toLowerCase().includes(t) ||
      s.email?.toLowerCase().includes(t);
    const c = Number(s.contratos_suscritos || 0);
    const m = Number(s.saldo_pesos_clp || 0);
    const cMinOk = contratosMin === "" || c >= Number(contratosMin);
    const cMaxOk = contratosMax === "" || c <= Number(contratosMax);
    const mMinOk = montoMin === "" || m >= Number(montoMin);
    const mMaxOk = montoMax === "" || m <= Number(montoMax);
    return textMatch && cMinOk && cMaxOk && mMinOk && mMaxOk;
  });

  const clearFilters = () => {
    setContratosMin("");
    setContratosMax("");
    setMontoMin("");
    setMontoMax("");
    setQ("");
  };

  const hasActiveFilters =
    q || contratosMin !== "" || contratosMax !== "" || montoMin !== "" || montoMax !== "";

  const remove = async (rut) => {
    try {
      await api.delete(`/shareholders/${encodeURIComponent(rut)}`);
      toast.success("Accionista eliminado");
      load();
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const resetOne = async (rut) => {
    try {
      await api.post(`/admin/shareholders/${encodeURIComponent(rut)}/reset-password`);
      toast.success("Contraseña reseteada a los primeros 4 del email");
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const resetAll = async () => {
    try {
      const r = await api.post(`/admin/reset-all-passwords`);
      toast.success(`${r.data.accounts_reset} contraseñas reseteadas`);
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-sm">
      <div className="px-6 py-4 border-b border-[#E2E8F0] flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <div>
            <h3 className="font-heading text-lg font-semibold text-[#0A2540]">Accionistas</h3>
            <p className="text-xs text-[#475569]">
              Listado completo con totales calculados · {filtered.length} de {items.length}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  className="rounded-sm border-[#E2E8F0] h-10"
                  data-testid="reset-all-passwords-button"
                >
                  <RefreshCw size={14} className="mr-2" />
                  Resetear contraseñas
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="rounded-sm">
                <AlertDialogHeader>
                  <AlertDialogTitle>Resetear todas las contraseñas</AlertDialogTitle>
                  <AlertDialogDescription>
                    Todas las cuentas de accionistas volverán a la contraseña inicial
                    (primeros 4 caracteres del email, en minúsculas; fallback: RUT).
                    Cada accionista deberá cambiarla al ingresar.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    className="rounded-sm bg-[#0A2540]"
                    onClick={resetAll}
                    data-testid="reset-all-passwords-confirm"
                  >
                    Resetear todas
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <div className="relative w-full sm:w-80">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]" />
              <Input
                placeholder="Buscar por nombre, RUT o email…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-9 rounded-sm h-10 border-[#E2E8F0]"
                data-testid="shareholders-search"
              />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 items-end">
          <RangeField
            label="Contratos desde"
            value={contratosMin}
            onChange={setContratosMin}
            placeholder="0"
            testid="filter-contratos-min"
          />
          <RangeField
            label="Contratos hasta"
            value={contratosMax}
            onChange={setContratosMax}
            placeholder="∞"
            testid="filter-contratos-max"
          />
          <RangeField
            label="Monto desde (CLP)"
            value={montoMin}
            onChange={setMontoMin}
            placeholder="0"
            testid="filter-monto-min"
          />
          <RangeField
            label="Monto hasta (CLP)"
            value={montoMax}
            onChange={setMontoMax}
            placeholder="∞"
            testid="filter-monto-max"
          />
          <Button
            variant="outline"
            onClick={clearFilters}
            disabled={!hasActiveFilters}
            className="rounded-sm h-10 border-[#E2E8F0]"
            data-testid="filters-clear"
          >
            Limpiar filtros
          </Button>
        </div>
      </div>
      <Table data-testid="shareholders-table">
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">#</TableHead>
            <TableHead>RUT</TableHead>
            <TableHead>Nombre</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Sociedad</TableHead>
            <TableHead className="text-right">Contratos</TableHead>
            <TableHead className="text-right">Acciones</TableHead>
            <TableHead className="text-right">Monto</TableHead>
            <TableHead className="text-right">—</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={9} className="text-center py-8 text-[#475569]">Cargando…</TableCell>
            </TableRow>
          ) : filtered.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} className="text-center py-12 text-[#475569]">
                {items.length === 0
                  ? "No hay accionistas. Cargue el Excel desde la pestaña \"Carga Excel\"."
                  : "Ningún accionista coincide con los filtros aplicados."}
              </TableCell>
            </TableRow>
          ) : (
            filtered.map((s, idx) => (
              <TableRow key={s.rut} data-testid={`shareholder-row-${s.rut}`}>
                <TableCell className="text-[#475569] font-mono text-xs">{idx + 1}</TableCell>
                <TableCell className="font-mono text-sm">{formatRut(s.rut)}</TableCell>
                <TableCell className="font-medium">{s.nombre}</TableCell>
                <TableCell className="text-[#475569] text-sm">{s.email || "-"}</TableCell>
                <TableCell className="text-sm">{s.sociedad || "-"}</TableCell>
                <TableCell className="text-right font-mono">{formatNumber(s.contratos_suscritos || 0)}</TableCell>
                <TableCell className="text-right font-mono">{formatNumber(s.saldo_acciones || 0)}</TableCell>
                <TableCell className="text-right font-mono font-semibold">{formatCLP(s.saldo_pesos_clp || 0)}</TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-sm h-8 border-[#E2E8F0]"
                      onClick={() => setViewing(s)}
                      data-testid={`view-shareholder-${s.rut}`}
                      title="Ver compras"
                    >
                      <Eye size={14} />
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-sm h-8 border-[#E2E8F0]" onClick={() => setEditing(s)} data-testid={`edit-shareholder-${s.rut}`}>
                      <Pencil size={14} />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-sm h-8 border-[#E2E8F0]"
                          data-testid={`reset-shareholder-${s.rut}`}
                          title="Resetear contraseña"
                        >
                          <KeyRound size={14} />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="rounded-sm">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Resetear contraseña</AlertDialogTitle>
                          <AlertDialogDescription>
                            La contraseña de <b>{s.nombre}</b> volverá a los primeros 4 caracteres
                            de su email (<span className="font-mono">{s.email || s.rut}</span>).
                            El accionista deberá cambiarla al ingresar.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            className="rounded-sm bg-[#0A2540]"
                            onClick={() => resetOne(s.rut)}
                          >
                            Resetear
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="outline" className="rounded-sm h-8 border-[#E2E8F0] text-[#DC2626]" data-testid={`delete-shareholder-${s.rut}`}>
                          <Trash2 size={14} />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="rounded-sm">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Eliminar accionista</AlertDialogTitle>
                          <AlertDialogDescription>
                            Esto eliminará al accionista, su cuenta y todas sus compras. ¿Continuar?
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
                          <AlertDialogAction className="rounded-sm bg-[#DC2626]" onClick={() => remove(s.rut)}>
                            Eliminar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <EditShareholderDialog
        shareholder={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          load();
          onChange?.();
        }}
      />
      <BreakdownDialog
        shareholder={viewing}
        onClose={() => setViewing(null)}
      />
    </div>
  );
}

function RangeField({ label, value, onChange, placeholder, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-[0.2em] text-[#475569]">{label}</Label>
      <Input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="rounded-sm h-10 border-[#E2E8F0]"
        data-testid={testid}
      />
    </div>
  );
}

function BreakdownDialog({ shareholder, onClose }) {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!shareholder) return;
    setLoading(true);
    setPurchases([]);
    api
      .get(`/purchases/${encodeURIComponent(shareholder.rut)}`)
      .then((r) => setPurchases(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [shareholder]);

  const totalAcciones = purchases.reduce((s, p) => s + (p.cantidad || 0), 0);
  const totalMonto = purchases.reduce((s, p) => s + (p.monto || 0), 0);
  const avgPrecio = totalAcciones ? totalMonto / totalAcciones : 0;

  return (
    <Dialog open={!!shareholder} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="rounded-sm sm:max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="breakdown-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-[#0A2540]">
            Composición de compras
          </DialogTitle>
          <DialogDescription className="sr-only">
            Desglose completo de las compras de acciones del accionista seleccionado.
          </DialogDescription>
        </DialogHeader>

        {shareholder && (
          <div className="flex-1 overflow-auto">
            {/* Header — shareholder info */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pb-4 border-b border-[#E2E8F0]">
              <InfoBlock label="Accionista" value={shareholder.nombre} />
              <InfoBlock label="RUT" value={formatRut(shareholder.rut)} mono />
              <InfoBlock label="Sociedad" value={shareholder.sociedad || "-"} />
              <InfoBlock label="Email" value={shareholder.email || "-"} />
            </div>

            {/* Summary KPIs */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 py-4 border-b border-[#E2E8F0]">
              <SummaryCard
                label="Compras registradas"
                value={formatNumber(purchases.length)}
                testid="breakdown-count"
              />
              <SummaryCard
                label="Acciones adquiridas"
                value={formatNumber(totalAcciones)}
                testid="breakdown-acciones"
              />
              <SummaryCard
                label="Monto total"
                value={formatCLP(totalMonto)}
                testid="breakdown-monto"
              />
              <SummaryCard
                label="Precio promedio"
                value={formatCLP(Math.round(avgPrecio))}
                testid="breakdown-promedio"
              />
            </div>

            {/* Table */}
            <div className="pt-4">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#475569] mb-3">
                Detalle histórico de compras
              </div>
              <div className="border border-[#E2E8F0] rounded-sm">
                <Table data-testid="breakdown-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>Fecha</TableHead>
                      <TableHead className="text-right">Cantidad</TableHead>
                      <TableHead className="text-right">Precio unit.</TableHead>
                      <TableHead className="text-right">Monto</TableHead>
                      <TableHead>Detalle</TableHead>
                      <TableHead>Origen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-[#475569]">
                          Cargando…
                        </TableCell>
                      </TableRow>
                    ) : purchases.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-10 text-[#475569]">
                          Sin compras registradas para este accionista.
                        </TableCell>
                      </TableRow>
                    ) : (
                      purchases.map((p, idx) => (
                        <TableRow key={p.id}>
                          <TableCell className="text-[#475569] font-mono text-xs">
                            {idx + 1}
                          </TableCell>
                          <TableCell className="font-medium">{p.fecha || "-"}</TableCell>
                          <TableCell className="text-right font-mono">
                            {formatNumber(p.cantidad)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-[#475569]">
                            {p.precio_unitario ? formatCLP(p.precio_unitario) : "-"}
                          </TableCell>
                          <TableCell className="text-right font-mono font-semibold">
                            {formatCLP(p.monto)}
                          </TableCell>
                          <TableCell className="text-sm text-[#475569]">
                            {p.detalle || "-"}
                          </TableCell>
                          <TableCell>
                            <span className="text-[10px] uppercase tracking-[0.2em] text-[#475569] border border-[#E2E8F0] px-2 py-0.5 rounded-sm">
                              {p.source === "excel" ? "Excel" : "Manual"}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="pt-4 border-t border-[#E2E8F0]">
          <Button
            variant="outline"
            className="rounded-sm border-[#E2E8F0]"
            onClick={onClose}
            data-testid="breakdown-close"
          >
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InfoBlock({ label, value, mono = false }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#475569]">{label}</div>
      <div className={`mt-1 text-sm text-[#0F172A] font-medium ${mono ? "font-mono" : ""}`}>
        {value || "-"}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-[#F8F9FA] border border-[#E2E8F0] rounded-sm p-4"
    >
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#475569]">{label}</div>
      <div className="mt-2 font-heading text-xl font-bold text-[#0A2540]">{value}</div>
    </div>
  );
}

function EditShareholderDialog({ shareholder, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (shareholder) setForm({
      nombre: shareholder.nombre || "",
      email: shareholder.email || "",
      direccion: shareholder.direccion || "",
      telefono: shareholder.telefono || "",
      sociedad: shareholder.sociedad || "",
      saldo_acciones: shareholder.saldo_acciones ?? 0,
      saldo_pesos_clp: shareholder.saldo_pesos_clp ?? 0,
      contratos_suscritos: shareholder.contratos_suscritos ?? 0,
    });
  }, [shareholder]);

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const body = {
        ...form,
        saldo_acciones: form.saldo_acciones === "" ? 0 : Number(form.saldo_acciones),
        saldo_pesos_clp: form.saldo_pesos_clp === "" ? 0 : Number(form.saldo_pesos_clp),
        contratos_suscritos: form.contratos_suscritos === "" ? 0 : Number(form.contratos_suscritos),
      };
      await api.put(`/shareholders/${encodeURIComponent(shareholder.rut)}`, body);
      toast.success("Accionista actualizado");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!shareholder} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-[#0A2540]">Editar accionista</DialogTitle>
        </DialogHeader>
        {shareholder && (
          <form onSubmit={save} className="space-y-4">
            <div className="text-xs text-[#475569]">RUT: <span className="font-mono text-[#0F172A]">{formatRut(shareholder.rut)}</span></div>
            <div className="grid grid-cols-2 gap-3">
              <FieldInput label="Nombre" value={form.nombre} onChange={(v) => setForm({ ...form, nombre: v })} />
              <FieldInput label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
              <FieldInput label="Teléfono" value={form.telefono} onChange={(v) => setForm({ ...form, telefono: v })} />
              <FieldInput label="Sociedad" value={form.sociedad} onChange={(v) => setForm({ ...form, sociedad: v })} />
              <div className="col-span-2">
                <FieldInput label="Dirección" value={form.direccion} onChange={(v) => setForm({ ...form, direccion: v })} />
              </div>
            </div>
            <div className="pt-2 border-t border-[#E2E8F0]">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#475569] mb-3">
                Saldos del accionista
              </div>
              <div className="grid grid-cols-3 gap-3">
                <FieldInput
                  label="Saldo acciones"
                  type="number"
                  value={form.saldo_acciones}
                  onChange={(v) => setForm({ ...form, saldo_acciones: v })}
                />
                <FieldInput
                  label="Saldo pesos CLP"
                  type="number"
                  value={form.saldo_pesos_clp}
                  onChange={(v) => setForm({ ...form, saldo_pesos_clp: v })}
                />
                <FieldInput
                  label="Contratos suscritos"
                  type="number"
                  value={form.contratos_suscritos}
                  onChange={(v) => setForm({ ...form, contratos_suscritos: v })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-sm" onClick={onClose}>Cancelar</Button>
              <Button type="submit" disabled={loading} className="rounded-sm bg-[#0A2540] hover:bg-[#051626]" data-testid="edit-shareholder-save">
                {loading ? "Guardando…" : "Guardar"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FieldInput({ label, value, onChange, type = "text", required }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-[0.2em] text-[#475569]">{label}</Label>
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        type={type}
        required={required}
        className="rounded-sm h-10 border-[#E2E8F0]"
      />
    </div>
  );
}

// ---------- Purchases ----------
function PurchasesSection({ onChange }) {
  const [shareholders, setShareholders] = useState([]);
  const [selectedRut, setSelectedRut] = useState("");
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    api.get("/shareholders").then((r) => setShareholders(r.data));
  }, []);

  useEffect(() => {
    if (!selectedRut) return;
    setLoading(true);
    api
      .get(`/purchases/${encodeURIComponent(selectedRut)}`)
      .then((r) => setPurchases(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [selectedRut]);

  const refresh = () => {
    if (selectedRut) {
      api.get(`/purchases/${encodeURIComponent(selectedRut)}`).then((r) => setPurchases(r.data));
    }
    onChange?.();
  };

  const remove = async (pid) => {
    try {
      await api.delete(`/purchases/${pid}`);
      toast.success("Compra eliminada");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-sm">
      <div className="px-6 py-4 border-b border-[#E2E8F0] flex flex-col sm:flex-row sm:items-end gap-3 justify-between">
        <div>
          <h3 className="font-heading text-lg font-semibold text-[#0A2540]">Compras de acciones</h3>
          <p className="text-xs text-[#475569]">Gestione el detalle de compras por accionista</p>
        </div>
        <div className="flex items-end gap-3">
          <div className="w-full sm:w-80">
            <Label className="text-[10px] uppercase tracking-[0.2em] text-[#475569]">Accionista</Label>
            <Select value={selectedRut} onValueChange={setSelectedRut}>
              <SelectTrigger className="rounded-sm h-10 border-[#E2E8F0] mt-1" data-testid="purchases-shareholder-select">
                <SelectValue placeholder="Seleccione un accionista" />
              </SelectTrigger>
              <SelectContent>
                {shareholders.map((s) => (
                  <SelectItem key={s.rut} value={s.rut}>
                    {s.nombre} · {formatRut(s.rut)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            disabled={!selectedRut}
            onClick={() => {
              setEditing(null);
              setOpen(true);
            }}
            className="rounded-sm bg-[#0A2540] hover:bg-[#051626]"
            data-testid="purchase-new-button"
          >
            <Plus size={16} className="mr-1" /> Nueva compra
          </Button>
        </div>
      </div>

      <Table data-testid="purchases-admin-table">
        <TableHeader>
          <TableRow>
            <TableHead>Fecha</TableHead>
            <TableHead className="text-right">Cantidad</TableHead>
            <TableHead className="text-right">Precio unit.</TableHead>
            <TableHead className="text-right">Monto</TableHead>
            <TableHead>Detalle</TableHead>
            <TableHead className="text-right">—</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {!selectedRut ? (
            <TableRow><TableCell colSpan={6} className="text-center py-12 text-[#475569]">Seleccione un accionista</TableCell></TableRow>
          ) : loading ? (
            <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#475569]">Cargando…</TableCell></TableRow>
          ) : purchases.length === 0 ? (
            <TableRow><TableCell colSpan={6} className="text-center py-12 text-[#475569]">Sin compras para este accionista</TableCell></TableRow>
          ) : purchases.map((p) => (
            <TableRow key={p.id}>
              <TableCell>{p.fecha}</TableCell>
              <TableCell className="text-right font-mono">{formatNumber(p.cantidad)}</TableCell>
              <TableCell className="text-right font-mono text-[#475569]">{p.precio_unitario ? formatCLP(p.precio_unitario) : "-"}</TableCell>
              <TableCell className="text-right font-mono font-semibold">{formatCLP(p.monto)}</TableCell>
              <TableCell className="text-sm text-[#475569]">{p.detalle || "-"}</TableCell>
              <TableCell className="text-right">
                <div className="inline-flex gap-1">
                  <Button size="sm" variant="outline" className="rounded-sm h-8 border-[#E2E8F0]" onClick={() => { setEditing(p); setOpen(true); }} data-testid={`edit-purchase-${p.id}`}>
                    <Pencil size={14} />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="outline" className="rounded-sm h-8 border-[#E2E8F0] text-[#DC2626]" data-testid={`delete-purchase-${p.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="rounded-sm">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Eliminar compra</AlertDialogTitle>
                        <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
                        <AlertDialogAction className="rounded-sm bg-[#DC2626]" onClick={() => remove(p.id)}>Eliminar</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <PurchaseDialog
        open={open}
        onOpenChange={setOpen}
        purchase={editing}
        rut={selectedRut}
        onSaved={() => {
          setOpen(false);
          refresh();
        }}
      />
    </div>
  );
}

function PurchaseDialog({ open, onOpenChange, purchase, rut, onSaved }) {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      if (purchase) {
        setForm({
          fecha: purchase.fecha || "",
          cantidad: purchase.cantidad || 0,
          monto: purchase.monto || 0,
          precio_unitario: purchase.precio_unitario || "",
          detalle: purchase.detalle || "",
        });
      } else {
        setForm({ fecha: new Date().toISOString().slice(0, 10), cantidad: 0, monto: 0, precio_unitario: "", detalle: "" });
      }
    }
  }, [open, purchase]);

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const body = {
        rut,
        fecha: form.fecha,
        cantidad: Number(form.cantidad),
        monto: Number(form.monto),
        precio_unitario: form.precio_unitario === "" ? null : Number(form.precio_unitario),
        detalle: form.detalle,
      };
      if (purchase) {
        await api.put(`/purchases/${purchase.id}`, body);
      } else {
        await api.post("/purchases", body);
      }
      toast.success("Compra guardada");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-[#0A2540]">{purchase ? "Editar" : "Nueva"} compra</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <FieldInput label="Fecha" type="date" value={form.fecha} onChange={(v) => setForm({ ...form, fecha: v })} required />
            <FieldInput label="Cantidad" type="number" value={form.cantidad} onChange={(v) => setForm({ ...form, cantidad: v })} required />
            <FieldInput label="Monto (CLP)" type="number" value={form.monto} onChange={(v) => setForm({ ...form, monto: v })} required />
            <FieldInput label="Precio unitario" type="number" value={form.precio_unitario} onChange={(v) => setForm({ ...form, precio_unitario: v })} />
            <div className="col-span-2">
              <FieldInput label="Detalle" value={form.detalle} onChange={(v) => setForm({ ...form, detalle: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={loading} className="rounded-sm bg-[#0A2540] hover:bg-[#051626]" data-testid="purchase-save-button">
              {loading ? "Guardando…" : "Guardar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Announcements ----------
function AnnouncementsSection({ onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/announcements").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/announcements/${id}`);
      toast.success("Comunicado eliminado");
      load();
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const download = async (id, filename) => {
    try {
      const token = localStorage.getItem("sn_token");
      const res = await fetch(`${API}/announcements/${id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        if (res.status === 401) throw new Error("Sesión expirada. Vuelva a iniciar sesión.");
        if (res.status === 404) throw new Error("El archivo ya no está disponible.");
        throw new Error(`No se pudo descargar (HTTP ${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "comunicado.pdf";
      a.rel = "noopener";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 1500);
    } catch (e) {
      toast.error(e.message || "Error al descargar");
    }
  };

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-sm">
      <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
        <div>
          <h3 className="font-heading text-lg font-semibold text-[#0A2540]">Comunicados</h3>
          <p className="text-xs text-[#475569]">Documentos oficiales disponibles para los accionistas</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm bg-[#0A2540] hover:bg-[#051626]" data-testid="announcement-new-button"><Plus size={16} className="mr-1" /> Nuevo comunicado</Button>
          </DialogTrigger>
          <UploadAnnouncementDialog
            onUploaded={() => {
              setOpen(false);
              load();
              onChange?.();
            }}
          />
        </Dialog>
      </div>

      <Table data-testid="announcements-table">
        <TableHeader>
          <TableRow>
            <TableHead>Título</TableHead>
            <TableHead>Descripción</TableHead>
            <TableHead>Archivo</TableHead>
            <TableHead>Fecha</TableHead>
            <TableHead className="text-right">—</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow><TableCell colSpan={5} className="text-center py-8 text-[#475569]">Cargando…</TableCell></TableRow>
          ) : items.length === 0 ? (
            <TableRow><TableCell colSpan={5} className="text-center py-12 text-[#475569]">Sin comunicados</TableCell></TableRow>
          ) : items.map((a) => (
            <TableRow key={a.id}>
              <TableCell className="font-medium">{a.titulo}</TableCell>
              <TableCell className="text-[#475569] text-sm">{a.descripcion || "-"}</TableCell>
              <TableCell className="text-sm text-[#475569]">{a.filename}</TableCell>
              <TableCell className="text-sm">{new Date(a.uploaded_at).toLocaleDateString("es-CL")}</TableCell>
              <TableCell className="text-right">
                <div className="inline-flex gap-1">
                  <Button size="sm" variant="outline" className="rounded-sm h-8 border-[#E2E8F0]" onClick={() => download(a.id, a.filename)} data-testid={`download-announcement-admin-${a.id}`}>
                    <Download size={14} />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="outline" className="rounded-sm h-8 border-[#E2E8F0] text-[#DC2626]" data-testid={`delete-announcement-${a.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="rounded-sm">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Eliminar comunicado</AlertDialogTitle>
                        <AlertDialogDescription>Se eliminará el archivo PDF de forma permanente.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
                        <AlertDialogAction className="rounded-sm bg-[#DC2626]" onClick={() => remove(a.id)}>Eliminar</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function UploadAnnouncementDialog({ onUploaded }) {
  const [titulo, setTitulo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Seleccione un PDF");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("titulo", titulo);
      fd.append("descripcion", descripcion);
      fd.append("file", file);
      await api.post("/announcements", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Comunicado publicado");
      setTitulo("");
      setDescripcion("");
      setFile(null);
      onUploaded?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <DialogContent className="rounded-sm">
      <DialogHeader>
        <DialogTitle className="font-heading text-[#0A2540]">Subir comunicado</DialogTitle>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <FieldInput label="Título" value={titulo} onChange={setTitulo} required />
        <div className="space-y-1.5">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-[#475569]">Descripción</Label>
          <Textarea rows={3} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} className="rounded-sm border-[#E2E8F0]" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-[#475569]">Archivo PDF</Label>
          <Input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="rounded-sm border-[#E2E8F0]" data-testid="announcement-file-input" />
        </div>
        <DialogFooter>
          <Button type="submit" disabled={loading} className="rounded-sm bg-[#0A2540] hover:bg-[#051626]" data-testid="announcement-upload-submit">
            {loading ? "Subiendo…" : "Publicar"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

// ---------- Solicitudes ----------
function SolicitudesSection({ onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = () => {
    setLoading(true);
    api.get("/solicitudes").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const updateEstado = async (id, estado) => {
    try {
      const fd = new FormData();
      fd.append("estado", estado);
      await api.put(`/solicitudes/${id}`, fd);
      toast.success("Estado actualizado");
      load();
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const respond = async (id, respuesta) => {
    await api.post(`/solicitudes/${id}/responder`, { respuesta });
    toast.success("Respuesta enviada");
    load();
    onChange?.();
  };

  const remove = async (id) => {
    try {
      await api.delete(`/solicitudes/${id}`);
      toast.success("Solicitud eliminada");
      load();
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filtered = items.filter((i) => filter === "all" || i.estado === filter);
  const counts = {
    all: items.length,
    pendiente: items.filter((i) => i.estado === "pendiente").length,
    respondida: items.filter((i) => i.estado === "respondida").length,
    descartada: items.filter((i) => i.estado === "descartada").length,
  };

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-sm">
      <div className="px-6 py-4 border-b border-[#E2E8F0] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="font-heading text-lg font-semibold text-[#0A2540]">Solicitudes recibidas</h3>
          <p className="text-xs text-[#475569]">
            Consultas enviadas por los accionistas desde su perfil
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5 text-[11px]" data-testid="solicitudes-filters">
          {[
            { key: "all", label: "Todas" },
            { key: "pendiente", label: "Pendientes" },
            { key: "respondida", label: "Respondidas" },
            { key: "descartada", label: "Descartadas" },
          ].map((f) => (
            <button
              key={f.key}
              type="button"
              data-testid={`solicitudes-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`rounded-sm px-3 py-1.5 border uppercase tracking-[0.2em] font-semibold transition-colors ${
                filter === f.key
                  ? "bg-[#0A2540] text-white border-[#0A2540]"
                  : "bg-white text-[#475569] border-[#E2E8F0] hover:text-[#0A2540]"
              }`}
            >
              {f.label} · {counts[f.key]}
            </button>
          ))}
        </div>
      </div>

      <div className="p-6 space-y-3" data-testid="admin-solicitudes-list">
        {loading ? (
          <div className="text-center py-10 text-[#475569]">Cargando…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-[#475569] border border-dashed border-[#E2E8F0] rounded-sm">
            {items.length === 0 ? "Sin solicitudes" : "No hay solicitudes en esta categoría"}
          </div>
        ) : (
          filtered.map((s) => (
            <AdminSolicitudCard
              key={s.id}
              item={s}
              onRespond={respond}
              onEstado={updateEstado}
              onDelete={remove}
            />
          ))
        )}
      </div>
    </div>
  );
}

function AdminSolicitudCard({ item, onRespond, onEstado, onDelete }) {
  const [respuesta, setRespuesta] = useState(item.respuesta || "");
  const [replying, setReplying] = useState(false);
  const alreadyResponded = item.estado === "respondida";

  const submit = async (e) => {
    e.preventDefault();
    if (!respuesta.trim()) return;
    setReplying(true);
    try {
      await onRespond(item.id, respuesta.trim());
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setReplying(false);
    }
  };

  const estadoStyle = {
    pendiente: "text-[#D97706] border-[#F59E0B]/30 bg-[#FFFBEB]",
    respondida: "text-[#16A34A] border-[#16A34A]/30 bg-[#F0FDF4]",
    descartada: "text-[#475569] border-[#E2E8F0] bg-[#F8F9FA]",
  }[item.estado] || "";

  return (
    <div className="border border-[#E2E8F0] rounded-sm" data-testid={`admin-solicitud-${item.id}`}>
      {/* Header row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-start p-4 border-b border-[#E2E8F0] bg-[#F8F9FA]">
        <div className="lg:col-span-3">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#475569]">Accionista</div>
          <div className="mt-1 text-sm font-medium text-[#0F172A]">{item.nombre}</div>
          <div className="text-[11px] font-mono text-[#475569]">{formatRut(item.rut)}</div>
        </div>
        <div className="lg:col-span-3">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#475569]">Email</div>
          <div className="mt-1 text-sm text-[#0F172A]">{item.email || "-"}</div>
        </div>
        <div className="lg:col-span-3">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#475569]">Enviada</div>
          <div className="mt-1 text-sm text-[#0F172A]">
            {new Date(item.created_at).toLocaleString("es-CL")}
          </div>
        </div>
        <div className="lg:col-span-3 flex flex-wrap items-center gap-2 lg:justify-end">
          <span className={`text-[10px] uppercase tracking-[0.2em] border px-2 py-1 rounded-sm ${estadoStyle}`}>
            {item.estado}
          </span>
          <Select value={item.estado} onValueChange={(v) => onEstado(item.id, v)}>
            <SelectTrigger
              className="h-8 rounded-sm border-[#E2E8F0] w-[140px]"
              data-testid={`solicitud-estado-${item.id}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pendiente">Pendiente</SelectItem>
              <SelectItem value="respondida">Respondida</SelectItem>
              <SelectItem value="descartada">Descartada</SelectItem>
            </SelectContent>
          </Select>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                className="rounded-sm h-8 border-[#E2E8F0] text-[#DC2626]"
                data-testid={`delete-solicitud-${item.id}`}
              >
                <Trash2 size={14} />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-sm">
              <AlertDialogHeader>
                <AlertDialogTitle>Eliminar solicitud</AlertDialogTitle>
                <AlertDialogDescription>
                  La solicitud será eliminada permanentemente.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  className="rounded-sm bg-[#DC2626]"
                  onClick={() => onDelete(item.id)}
                >
                  Eliminar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Message */}
      <div className="p-4">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#475569]">Mensaje</div>
        <p className="mt-1.5 text-sm text-[#0F172A] whitespace-pre-wrap">{item.mensaje}</p>
      </div>

      {/* Response section */}
      <div className="px-4 pb-4">
        {alreadyResponded && item.responded_at && (
          <div className="mb-3 text-[10px] uppercase tracking-[0.25em] text-[#16A34A]">
            Respondida el {new Date(item.responded_at).toLocaleString("es-CL")}
          </div>
        )}
        <form onSubmit={submit} className="space-y-2">
          <Label className="text-[10px] uppercase tracking-[0.25em] text-[#475569]">
            {alreadyResponded ? "Respuesta (editable)" : "Redactar respuesta"}
          </Label>
          <Textarea
            rows={3}
            value={respuesta}
            onChange={(e) => setRespuesta(e.target.value)}
            placeholder="Escriba la respuesta al accionista…"
            className="rounded-sm border-[#E2E8F0]"
            data-testid={`respuesta-input-${item.id}`}
          />
          <div className="flex items-center justify-end">
            <Button
              type="submit"
              size="sm"
              disabled={replying || !respuesta.trim()}
              className="rounded-sm bg-[#0A2540] hover:bg-[#051626] text-white"
              data-testid={`respuesta-submit-${item.id}`}
            >
              {replying
                ? "Enviando…"
                : alreadyResponded
                ? "Actualizar respuesta"
                : "Enviar respuesta"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------- Audit Log ----------
const ACTION_LABEL = {
  create: "Crear",
  update: "Editar",
  delete: "Eliminar",
  reset_password: "Reset contraseña",
  reset_all_passwords: "Reset masivo contraseñas",
  response: "Responder",
};
const ENTITY_LABEL = {
  shareholder: "Accionista",
  shareholders: "Accionistas",
  purchase: "Compra",
  solicitud: "Solicitud",
  announcement: "Comunicado",
};
const ACTION_COLOR = {
  create: "text-[#16A34A] border-[#16A34A]/30 bg-[#F0FDF4]",
  update: "text-[#0A2540] border-[#0A2540]/20 bg-[#F1F5F9]",
  delete: "text-[#DC2626] border-[#DC2626]/30 bg-[#FEF2F2]",
  reset_password: "text-[#B4975A] border-[#B4975A]/30 bg-[#FAF7F0]",
  reset_all_passwords: "text-[#B4975A] border-[#B4975A]/30 bg-[#FAF7F0]",
  response: "text-[#0A2540] border-[#0A2540]/20 bg-[#F1F5F9]",
};

function AuditLogSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = () => {
    setLoading(true);
    api
      .get("/admin/audit-log?limit=500")
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = items.filter((i) =>
    filter === "all"
      ? true
      : filter === "passwords"
      ? i.action.startsWith("reset_")
      : i.action === filter
  );
  const counts = {
    all: items.length,
    create: items.filter((i) => i.action === "create").length,
    update: items.filter((i) => i.action === "update").length,
    delete: items.filter((i) => i.action === "delete").length,
    passwords: items.filter((i) => i.action.startsWith("reset_")).length,
  };

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-sm">
      <div className="px-6 py-4 border-b border-[#E2E8F0] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <History size={18} className="text-[#0A2540]" />
            <h3 className="font-heading text-lg font-semibold text-[#0A2540]">
              Registro de cambios
            </h3>
          </div>
          <p className="text-xs text-[#475569] mt-1">
            Historial de acciones del administrador sobre accionistas, compras y contraseñas.
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5 text-[11px]" data-testid="audit-filters">
          {[
            { key: "all", label: "Todos" },
            { key: "create", label: "Creaciones" },
            { key: "update", label: "Ediciones" },
            { key: "delete", label: "Eliminaciones" },
            { key: "passwords", label: "Contraseñas" },
          ].map((f) => (
            <button
              key={f.key}
              type="button"
              data-testid={`audit-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`rounded-sm px-3 py-1.5 border uppercase tracking-[0.2em] font-semibold transition-colors ${
                filter === f.key
                  ? "bg-[#0A2540] text-white border-[#0A2540]"
                  : "bg-white text-[#475569] border-[#E2E8F0] hover:text-[#0A2540]"
              }`}
            >
              {f.label} · {counts[f.key]}
            </button>
          ))}
          <Button
            variant="outline"
            onClick={load}
            className="rounded-sm h-8 border-[#E2E8F0] text-[11px]"
            data-testid="audit-refresh"
          >
            <RefreshCw size={12} className="mr-1" /> Actualizar
          </Button>
        </div>
      </div>

      <Table data-testid="audit-table">
        <TableHeader>
          <TableRow>
            <TableHead className="w-48">Fecha</TableHead>
            <TableHead>Administrador</TableHead>
            <TableHead>Acción</TableHead>
            <TableHead>Entidad</TableHead>
            <TableHead>Detalle</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={5} className="text-center py-8 text-[#475569]">
                Cargando…
              </TableCell>
            </TableRow>
          ) : filtered.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-center py-12 text-[#475569]">
                {items.length === 0
                  ? "Sin registros. Las acciones del admin se irán registrando aquí."
                  : "Sin registros en esta categoría."}
              </TableCell>
            </TableRow>
          ) : (
            filtered.map((log) => (
              <TableRow key={log.id} data-testid={`audit-row-${log.id}`}>
                <TableCell className="text-xs text-[#475569] font-mono">
                  {new Date(log.created_at).toLocaleString("es-CL")}
                </TableCell>
                <TableCell className="text-sm">
                  <div className="font-medium text-[#0F172A]">{log.admin_name}</div>
                  <div className="text-[11px] text-[#475569]">{log.admin_email}</div>
                </TableCell>
                <TableCell>
                  <span
                    className={`text-[10px] uppercase tracking-[0.2em] border px-2 py-1 rounded-sm font-semibold ${
                      ACTION_COLOR[log.action] || ""
                    }`}
                  >
                    {ACTION_LABEL[log.action] || log.action}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="text-sm">{ENTITY_LABEL[log.entity] || log.entity}</div>
                  <div className="text-[11px] font-mono text-[#475569]">{log.entity_id}</div>
                </TableCell>
                <TableCell>
                  <AuditDetails details={log.details} />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function AuditDetails({ details }) {
  if (!details || Object.keys(details).length === 0) {
    return <span className="text-xs text-[#475569]">-</span>;
  }
  const changes = details.changes;
  return (
    <div className="text-xs space-y-1">
      {details.nombre && (
        <div className="text-[#0F172A]">
          <span className="text-[#475569]">Accionista: </span>
          <span className="font-medium">{details.nombre}</span>
        </div>
      )}
      {details.fecha && (
        <div className="text-[#475569]">
          Fecha: <span className="text-[#0F172A] font-mono">{details.fecha}</span>
        </div>
      )}
      {details.cantidad != null && (
        <div className="text-[#475569]">
          Cantidad: <span className="text-[#0F172A] font-mono">{formatNumber(details.cantidad)}</span>
        </div>
      )}
      {details.monto != null && (
        <div className="text-[#475569]">
          Monto: <span className="text-[#0F172A] font-mono">{formatCLP(details.monto)}</span>
        </div>
      )}
      {details.purchases_removed != null && (
        <div className="text-[#475569]">
          Compras eliminadas:{" "}
          <span className="text-[#0F172A] font-mono">{details.purchases_removed}</span>
        </div>
      )}
      {details.accounts_reset != null && (
        <div className="text-[#475569]">
          Cuentas reseteadas:{" "}
          <span className="text-[#0F172A] font-mono">{details.accounts_reset}</span>
        </div>
      )}
      {changes && typeof changes === "object" && (
        <div className="pt-1">
          {Object.entries(changes).map(([field, v]) => (
            <div key={field} className="flex items-center gap-2 text-[11px]">
              <span className="text-[#475569] uppercase tracking-[0.15em]">{field}:</span>
              <span className="font-mono text-[#DC2626] line-through">{formatDiffVal(v.from)}</span>
              <span className="text-[#475569]">→</span>
              <span className="font-mono text-[#16A34A]">{formatDiffVal(v.to)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatDiffVal(v) {
  if (v === null || v === undefined || v === "") return "∅";
  if (typeof v === "number") return formatNumber(v);
  return String(v);
}

